import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {FeatureTypeService, NotificationsService, ProjectService} from '@catcore/services';
import {Feature} from '@catcore/services/feature/feature';
import {
  FeatureCreateResultType,
  GuiZone,
  Project,
  ProjectFeatureCreateConstraint,
  ProjectFeatureCreateResult,
  ProjectFeatureCreateSelection,
} from '@catcore/types/api-v2';
import {NotificationMessage} from '@catcore/types/notifications';
import {buildSelectItemsBy} from '@catcore/utils/form-utils';
import {ProjectFeature} from '@catmodules/models/project-feature/types';
import {Control} from '@catmodules/widgets/slide-in-modal/types';
import {ActiveModal} from '@catmodules/widgets/slide-in-modal/utils/modal-ref';
import {LinkItem, StyleType} from '@catui/types';
import {SelectItem} from 'primeng/primeng';
import {isNullOrUndefined} from 'util';
import {sortByLabelFunc} from "@catcore/utils";
import {Subscription} from "rxjs";

/**
 * Component for the process to applying Feature to the Gui Zone of a Project
 */
@Component({
  templateUrl: './feature-to-project.component.html',
  styleUrls: ['./feature-to-project.component.scss'],
})
export class FeatureToProjectComponent implements OnInit, OnDestroy {

  @Input() fromFeature: boolean = true;
  @Input() feature: Feature;
  @Input() project: Project;

  title = 'Add Feature to Project and GUI Zone';
  styleType = StyleType;

  labels = {
    submit: 'Assign to project',
    project: 'Project',
    feature: 'Feature',
    guiZone: 'GUI Zone',
    portRef: 'Port',
  };

  projectFeatureItemLinks: LinkItem[];
  createdProjectFeatures: ProjectFeature[];
  unfullfilledConstraints: ProjectFeatureCreateConstraint[];
  missingSelections: ProjectFeatureCreateSelection[];

  private projectSubscription: Subscription = null;
  private featureSubscription: Subscription = null;


  get hasCreatedProjectFeatures() {
    return this.projectFeatureItemLinks && this.projectFeatureItemLinks.length;
  }

  controls: Control[] = [
    {
      isEnabled: () => this.readyToSubmit,
      label: this.labels.submit,
      onClick: ($event) => this.addToProject($event),
      styleType: this.styleType.success,
    },
  ];

  projects: Project[];
  form: FormGroup;
  featureSelectItems: SelectItem[] = [];
  projectSelectItems: SelectItem[] = [];
  guiZoneSelectItems: SelectItem[] = [];

  private portSelectItemsByRef: Map<string, SelectItem[]>;

  get readyToSubmit() {
    let isValid = !isNullOrUndefined(this.form);
    isValid = isValid && this.form.valid;
    return isValid;
  }

  constructor(private route: ActivatedRoute,
              private projectService: ProjectService,
              private router: Router,
              private modal: ActiveModal,
              private formBuilder: FormBuilder,
              private featureService: FeatureTypeService,
              private notificationService: NotificationsService) {

  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      project: this.formBuilder.control('', [Validators.required]),
      feature: this.formBuilder.control('', [Validators.required]),
      guiZone: this.formBuilder.control('', [Validators.required]),
      featureName: this.formBuilder.control('', [Validators.required]),
      selections: this.formBuilder.array([]),
    });

    if (this.fromFeature) {
      this.form.patchValue({
        feature: this.feature,
        featureName: this.buildDefaultFeatureName(this.feature)
      });
      this.projectSubscription = this.projectService
        .getAll()
        .delay(100)
        .subscribe((projects: Project[]) => this.updateProjectSelectItems(projects));
    } else {
      this.form.patchValue({
        project: this.project
      });

      this.updateGuiZoneSelectItems(this.projectService.extractGuiZonesFrom(this.project));

      this.featureSubscription = this.featureService.getAll()
        .subscribe((features: Feature[]) => this.updateFeatureSelectItems(features));
    }
  }

  ngOnDestroy() {
    if (this.projectSubscription) {
      this.projectSubscription.unsubscribe();
    }
    if (this.featureSubscription) {
      this.featureSubscription.unsubscribe();
    }
  }

  onChangeProject($event) {
    const project = $event.value;
    this.updateGuiZoneSelectItems(this.projectService.extractGuiZonesFrom(project));
  }

  onFeatureUpdate($event) {
    this.form.patchValue({
      featureName: this.buildDefaultFeatureName($event.value)
    });
  }

  /**
   * Executes the process to assign a Feature to a Project.
   *
   * It creates the necessary payload and handles the incoming response.
   *
   * The process can conain one or more steps, based on the state of the Feature and the requirements of the target Project / Gui Zone.
   *
   * With this in mind it can happens that the payload needs to be increaes based on requirements required through the WebAPI after the
   * first request tryout.
   *
   * @param $event
   */
  addToProject($event) {

    const payload = {
      project: (this.fromFeature ? this.form.value.project : this.project),
      feature: (this.fromFeature ? this.feature : this.form.value.feature),
      featureName: this.form.value.featureName,
      guiZone: this.form.value.guiZone,
      selections: this.form
        .get('selections')
        .value
        .map((values: { reference: string, portRef: number }) => {
          return {
            reference: values.reference,
            selected: values.portRef.toString(),
          };
        }),
    };

    this.projectService
      .createFeature(payload)
      .subscribe((result: ProjectFeatureCreateResult) => {
        const project: Project = (this.fromFeature ? this.form.value.project : this.project);
        const message = messageForProjectFeatureCreateResult(result, project);

        this.createdProjectFeatures = result.features;
        this.unfullfilledConstraints = result.constraints;
        this.updateMissingSelections(result.selections);

        this.projectFeatureItemLinks = result
          .features
          .map((item: ProjectFeature): LinkItem => {
            return {label: item.name, navigate: navigateToProjectFeature(item, project, this.modal, this.router)};
          });

        if ([FeatureCreateResultType.success].includes(result.status)) {
          this.notificationService.success(message.title, message.message);
        }
        if ([FeatureCreateResultType.missingSelections, FeatureCreateResultType.unfullfiledConstraints].includes(result.status)) {
          this.notificationService.warn(message.title, message.message);
        }
        if ([FeatureCreateResultType.failed].includes(result.status)) {
          this.notificationService.error(message.title, message.message);
        }
      })
    ;
  }

  findSelectItemsForKey(ref: string): SelectItem[] {
    let items: SelectItem[] = this.portSelectItemsByRef.get(ref);

    if (isNullOrUndefined(items)) {
      items = [];
    }

    return items;
  }

  private updateMissingSelections(selections: ProjectFeatureCreateSelection[]) {
    this.missingSelections = selections;
    this.portSelectItemsByRef = new Map<string, SelectItem[]>();
    const items: FormGroup[] = [];

    this.missingSelections.forEach((item: ProjectFeatureCreateSelection) => {
      items.push(this.formBuilder.group({
        label: this.formBuilder.control(item.name, [Validators.required]),
        reference: this.formBuilder.control(item.reference, [Validators.required]),
        portRef: this.formBuilder.control('', [Validators.required]),
      }));

      this.portSelectItemsByRef.set(item.reference, item.options);
    });
    this.form.setControl('selections', this.formBuilder.array(items));
  }

  private updateProjectSelectItems(projects: Project[]) {
    this.projectSelectItems = buildSelectItemsBy<Project>(
      projects,
      (item: Project) => createSelectItem(item));
  }

  private updateFeatureSelectItems(features: Feature[]) {
    this.featureSelectItems = buildSelectItemsBy<Feature>(
      features,
      (item: Feature) => createSelectItem(item));
    this.featureSelectItems.sort(sortByLabelFunc);
  }

  private updateGuiZoneSelectItems(guiZones: GuiZone[]) {
    this.guiZoneSelectItems = buildSelectItemsBy<GuiZone>(
      guiZones,
      (item: GuiZone) => createSelectItem(item));
    this.guiZoneSelectItems.sort(sortByLabelFunc);
  }

  private buildDefaultFeatureName(feature: Feature) {
    return feature.name + ' ' + (new Date()).toISOString();
  }
}

const createSelectItem = (item: any) => {
  return {
    label: item.name,
    value: item,
  };
};

const navigateToProjectFeature = (item: ProjectFeature, project: Project, activeModal: ActiveModal, router: Router) => {
  return () => {
    router.navigate(['projects', project.id, 'features']);
    activeModal.close();
  };
};

const messageForProjectFeatureCreateResult = (result: ProjectFeatureCreateResult, project: Project): NotificationMessage => {
  const messages = new Map<FeatureCreateResultType, NotificationMessage>();
  messages.set(
    FeatureCreateResultType.success,
    {title: 'New Project Feature created', message: `Feature successfully added to project ${project.name}`},
  );
  messages.set(
    FeatureCreateResultType.unfullfiledConstraints,
    {
      title: 'New Project Feature failed',
      message: `Some constraints have to be fulfilled before assigning this feature to the project`,
    },
  );
  messages.set(
    FeatureCreateResultType.missingSelections,
    {title: 'New Project Feature failed', message: `Some ports and/or units need to be defined to add this Feature to the Project`},
  );

  messages.set(
    FeatureCreateResultType.failed,
    {title: 'New Project Feature failed', message: `Something went terribly wrong`},
  );

  return messages.get(result.status);
};



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/features/components/feature-to-project/feature-to-project.component.ts